<template>
	<div class="product-belongs-to">
		<div class="p-4" v-if="isFetchingMembers">
			<div class="is-loading large dark"></div>
		</div>
		<template v-else>
			<div v-if="distribution && members.length > 0">
				<ul class="nav nav-tabs nav-fill" role="tablist">
					<li class="nav-item" role="tab" v-for="role in roles" :key="role.value" @click="selectedRole = role">
						<a class="nav-link" :class="{ 'active': role.value === selectedRole.value  }">{{ role.label }}</a>
					</li>
				</ul>
				<div class="tab-content">
					<div class="box mb-0 bt-0">
						<div class="product-belongs-to-members">
							<div class="form-group mb-3">
								<multiselect
									class="form-control"
									label="name"
									track-by="_id"
									:placeholder="`Select ${selectedRole.label}`"
									:reset-after="true"
									:options="options"
									:show-labels="false"
									:loading="isFetchingMembers"
									@select="addBelongsTo">
								</multiselect>
							</div>

							<!-- Selected -->
							<div class="selected-options" v-if="belongsToMembers.length > 0">
								<template v-if="selectedRole.value === csRole">
									<div
										class="selected-option product-belongs-to-member"
										v-for="(member, index) in teamMembersCS"
										:class="[`role-${member.role}`]"
										:key="index">
										<div>
											<span class="text-semibold">{{ member.name }}</span>
											<span
												class="ml-1"
												v-if="!!getQuota(member._id) && (distribution.type === 'percentage' || distribution.type === 'fixed')">
												(Order's Left: {{ getQuota(member._id) }})
											</span>
											<i class="material-icons selected-option-remove" v-tooltip="'Remove'" @click="removeBelongsTo(member._id)">close</i>
										</div>
										<!-- Percentage -->
										<div
											class="product-belongs-to-distribution row"
											v-if="selectedRole.value === csRole && distribution.type === 'percentage'">
											<div class="col-1">
												<span>{{ distributionPercentages[index] }}%</span>
											</div>
											<div class="col-11">
												<vue-slider
													class="member-distribution-slider"
													v-model="distributionPercentages[index]"
													:value="0"
													:min="0"
													:max="100"
													:dotSize="16"
													:height="8"
													:lazy="false"
													:tooltip="'focus'"
													:reverse="false"
													:tooltipFormatter="`Order's Distribution: {value}%`"
													:processStyle="{ backgroundColor: '#75cfd8' }"
													:tooltipStyle="{ backgroundColor: '#4e4e4e', borderColor: '#4e4e4e' }"
													@dragging="onSliderChange">
												</vue-slider>
											</div>
										</div>
										<!-- Rate -->
										<div
											class="product-belongs-to-distribution row"
											v-if="selectedRole.value === csRole && distribution.type === 'fixed'">
											<div class="col-1">
												<span>{{ rates[index] }}</span>
											</div>
											<div class="col-11">
												<vue-slider
													class="member-distribution-slider"
													v-model="rates[index]"
													:value="0"
													:min="0"
													:max="10"
													:dotSize="16"
													:height="8"
													:lazy="false"
													:tooltip="'focus'"
													:reverse="false"
													:tooltipFormatter="`Order's Rate: {value}`"
													:processStyle="{ backgroundColor: '#75cfd8' }"
													:tooltipStyle="{ backgroundColor: '#4e4e4e', borderColor: '#4e4e4e' }"
													@dragging="onRateChange">
												</vue-slider>
											</div>
										</div>
									</div>
								</template>
								<template v-else>
									<div
										class="selected-option product-belongs-to-member"
										v-for="(member, index) in belongsToMembers"
										:class="[`role-${member.role}`]"
										:key="index">
										<div>
											<span class="text-semibold">{{ member.name }}</span>
											<span
												class="ml-1"
												v-if="!!getQuota(member._id) && (distribution.type === 'percentage' || distribution.type === 'fixed')">
												(Order's Left: {{ getQuota(member._id) }})
											</span>
											<i class="material-icons selected-option-remove" v-tooltip="'Remove'" @click="removeBelongsTo(member._id)">close</i>
										</div>
									</div>
								</template>
							</div>

							<!-- Empty -->
							<div class="modal-empty-data text-center" v-else>{{ emptyMessage }}</div>

							<!-- Order's Distribution -->
							<div v-if="selectedRole.value === csRole && belongsToMembers.length > 0">
								<div class="mb-2 text-right" v-if="distribution.type === 'percentage'">
									<div><span>Total Percentages: <b :class="{ 'text-success': totalPercentages === 100, 'text-danger': totalPercentages !== 100 }">{{ totalPercentages }}%</b> <span class="info has-tooltip" v-tooltip="'Total percentages must be <b>100%</b>'"><i class="material-icons">info</i></span></span></div>
								</div>
								<div class="mb-2 text-right" v-if="distribution.type === 'fixed'">
									<div><span>Total Rates: <b :class="{ 'text-success': totalRates > 0, 'text-danger': totalRates === 0 }">{{ totalRates }}</b> <span class="info has-tooltip" v-tooltip="'Total rates must be > 0</b>'"><i class="material-icons">info</i></span></span></div>
								</div>
								<label>Order's Distribution</label>
								<div class="btn-group btn-group-block">
									<!-- Equals -->
									<button
										type="button"
										class="btn btn-light w-50"
										:class="{ 'active': distribution.type === 'equal' }"
										@click="setType('equal')">Equals</button>
									<!-- Percentage -->
									<button
										type="button"
										class="btn btn-light w-50"
										:class="{ 'active': distribution.type === 'percentage' }"
										@click="setType('percentage')">Percentage</button>
									<!-- Fixed -->
									<button
										type="button"
										class="btn btn-light w-50"
										:class="{ 'active': distribution.type === 'fixed' }"
										@click="setType('fixed')">Fixed</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="modal-empty-data integration-warning m-0 text-center" v-if="isMembersFetched && members.length === 0">
				<p class="mb-3">To assign product to Team Member, you must add Member to your team</p>
				<router-link :to="{ name: 'memberCreator' }" class="btn btn-primary">Add Team Member</router-link>
			</div>
		</template>
	</div>
</template>

<script>
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
import {
	memberRoles2,
	defaultOrderDistribution as defaultDistribution,
} from '../lib/statics';
import { duplicateVar } from '../lib/helper';
import teamApi from '../api/team';
import productsApi from '../api/products';

export default {
	name: 'productBelongsTo',
	components: {
		VueSlider,
	},
	props: {
		show: {
			type: Boolean,
			default: true,
		},
		productId: {
			type: String,
			default: '',
		},
		productIds: {
			type: Array,
			default: () => [],
		},
		requestData: {
			type: Boolean,
			default: true,
		},
		members: {
			type: Array,
			default: () => [],
		},
		belongsTo: {
			type: Array,
			default: () => [],
		},
		distribution: {
			type: Object,
			default: () => defaultDistribution,
		},
	},
	data() {
		return {
			roles: memberRoles2,
			selectedRole: memberRoles2[0],
			// members: [],
			// belongsTo: [],
			// distribution: null,
			distributionPercentages: [],
			rates: [],
			isSaving: false,
			isFetchingBelongsTo: false,
			isFetchingMembers: false,
			isMembersFetched: false,
			isAllMembersFetched: false,
			page: 1,
			limit: 100,
		};
	},
	methods: {
		close() {
			this.$emit('update:show', false);
		},
		fetchData() {
			if (this.productId && this.productId !== '') {
				this.fetchBelongsTo();
			} else {
				this.$emit('update:distribution', defaultDistribution);
			}
			this.fetchMembers();
		},
		fetchBelongsTo() {
			this.isFetchingBelongsTo = true;
			const callback = (response) => {
				const data = response.data;
				const belongsTo = data.belongs_to;
				const orderDistribution = data.order_distribution;
				if (!orderDistribution.team_members) {
					orderDistribution.team_members = [];
				}
				// this.belongsTo = belongsTo;
				// this.distribution = orderDistribution;
				this.$emit('update:belongsTo', belongsTo);
				this.$emit('update:distribution', orderDistribution);

				this.isFetchingBelongsTo = false;
			};
			const errorCallback = () => {
				this.isFetchingBelongsTo = false;
			};
			productsApi.getBelongsTo(this.productId, callback, errorCallback);
		},
		fetchMembers() {
			this.isFetchingMembers = true;
			const params = {
				limit: this.limit,
				sort_by: 'name',
				sort: 'asc',
				page: this.page,
				keyword: '',
			};
			const callback = (response) => {
				const items = response.data;
				// this.members = items;
				const members = duplicateVar(this.members);
				const updatedMembers = members.concat(items);

				this.$emit('update:members', updatedMembers);
				// this.isFetchingMembers = false;
				// this.isMembersFetched = true;

				this.page++;
				const isAll = items.length < this.limit;

				this.isAllMembersFetched = isAll;

				if (!isAll) {
					this.fetchMembers();
				} else {
					this.isFetchingMembers = false;
					this.isMembersFetched = true;
				}
			};
			const errorCallback = () => {
				this.isFetchingMembers = false;
			};
			teamApi.getMembers(params, callback, errorCallback);
		},
		addBelongsTo(member) {
			const index = this.belongsToMembers.findIndex(currMember => currMember._id === member._id);
			const isNotExist = index === -1;
			if (isNotExist) {
				const theMember = {
					_id: member._id,
					name: member.name,
					role: member.role,
				};

				this.belongsTo.push(theMember);

				const distribution = duplicateVar(this.orderDistribution);
				this.$emit('update:distribution', distribution);
			}
		},
		removeBelongsTo(id) {
			const index = this.belongsTo.findIndex(member => member._id === id);
			if (index !== -1) {
				this.distributionPercentages.splice(index, 1);
				this.belongsTo.splice(index, 1);
			}

			const distribution = duplicateVar(this.orderDistribution);

			if (this.teamMembersCS.length === 0) {
				distribution.type = 'equal';
			}

			this.$emit('update:distribution', distribution);
		},
		onSave() {
			if (this.isValid) {
				this.save();
			} else {
				let message = 'CS distribution\s percentage total must be 100%';
				if (this.distribution.type === 'fixed') {
					message = 'CS distribution\s rate total must be greater than 0';
				}
				this.$notify({
					group: 'productDetails',
					title: 'Order\s Distribution',
					text: message,
					type: 'error',
				});
			}
		},
		save() {
			this.isSaving = true;

			const ids = this.belongsTo.map(member => member._id);

			const belongsTo = JSON.stringify(ids);
			const distribution = JSON.stringify(this.orderDistribution);

			const params = {
				belongs_to: belongsTo,
				order_distribution: distribution,
			};
			const callback = () => {
				this.isSaving = false;
				this.$notify({
					group: 'app',
					title: 'Product\'s Assignation',
					text: 'Successfully Updated',
					type: 'success',
				});
			};
			const errorCallback = () => {
				this.isSaving = false;
			};
			if (this.productId) {
				productsApi.updateBelongsTo(this.productId, params, callback, errorCallback);
			} else if (this.productIds) {
				params.product_ids = JSON.stringify(this.productIds);
				productsApi.updateBelongsToBulk(params, callback, errorCallback);
			}
		},
		onSliderChange() {
			this.update();
		},
		onRateChange() {
			this.update();
		},
		update() {
			this.$emit('update:distribution', this.orderDistribution);
		},
		getQuota(userId) {
			const teamMembers = this.distribution.team_members ? this.distribution.team_members : [];
			const teamMember = teamMembers.find(teamMember => teamMember.user_id === userId);
			const quota = teamMember ? teamMember.quota : false;
			return quota;
		},
		setType(type) {
			this.distribution.type = type;
			this.update();
		},
	},
	computed: {
		options() {
			const filter = (member) => {
				const isSameRole = member.role === this.selectedRole.value;
				const index = this.belongsToMembers.findIndex(currMember => currMember._id === member._id);
				const isNotExist = index === -1;
				return isSameRole && isNotExist;
			};
			return this.members.filter(filter);
		},
		belongsToMembers() {
			const selectedRole = this.selectedRole.value;
			const filter = member => member.role === selectedRole;
			let members = this.belongsTo.filter(filter);
			if (selectedRole === this.csRole) {
				const distributions = this.distribution.team_members ? this.distribution.team_members : [];
				const sort = (firstMember, secondMember) => {
					const firstIndex = distributions.findIndex(distributionMember => distributionMember.user_id === firstMember._id);
					const secondIndex = distributions.findIndex(distributionMember => distributionMember.user_id === secondMember._id);
					const isGreater = firstIndex > secondIndex ? 1 : -1;
					return isGreater;
				};
				const sortedMembers = members.sort(sort);
				members = sortedMembers;
			}
			return members;
		},
		emptyMessage() {
			return `No ${this.selectedRole.label} assigned to this product`;
		},
		totalPercentages() {
			return this.distributionPercentages.reduce((total, amount) => total + amount, 0);
		},
		totalRates() {
			return this.rates.reduce((total, amount) => total + amount, 0);
		},
		isValid() {
			const isNoCs = this.belongsTo.length === 0;
			const isPercentageValid = this.distribution.type === 'percentage' && this.totalPercentages === 100;
			const isEqual = this.distribution.type === 'equal';
			const isFixed = this.distribution.type === 'fixed';
			const isFixedValid = isFixed && this.totalRates > 0;
			const valid = isEqual || isPercentageValid || isFixedValid || isNoCs;
			return valid;
		},
		teamMembersCS() {
			const distributions = this.distribution.team_members ? this.distribution.team_members : [];
			const sorter = (firstMember, secondMember) => {
				const firstIndex = distributions.findIndex(distributionMember => distributionMember.user_id === firstMember._id);
				const secondIndex = distributions.findIndex(distributionMember => distributionMember.user_id === secondMember._id);
				const isGreater = firstIndex > secondIndex ? 1 : -1;
				return isGreater;
			};
			const members = this.belongsTo
				.filter(member => member.role === this.csRole)
				.sort(sorter);
			return members;
		},
		orderDistribution() {
			const mapper = (member, index) => {
				const percentage = this.distributionPercentages[index] || 0;
				const rate = this.rates[index] || 0;
				const newMember = {
					user_id: member._id,
					percentage,
					rate,
				};
				return newMember;
			};
			const teamMembers = this.teamMembersCS.map(mapper);
			const distribution = duplicateVar(this.distribution);
			distribution.team_members = teamMembers;
			return distribution;
		},
		csRole() {
			return 'owner_cs';
		},
	},
	watch: {
		$props: {
			handler() {
				const members = this.distribution.team_members || [];
				const percentages = members.map(({ percentage }) => percentage);
				const rates = members.map(({ rate }) => rate || 0);
				this.distributionPercentages = percentages;
				this.rates = rates;
				this.isMembersFetched = true;
			},
			deep: true,
			immediate: true,
		},
		show(show) {
			if (show) {
				this.fetchData();
			}
		},
		members() {
			if (this.members.length > 0 && this.belongsTo.length === 0) {
				const assignedMembers = this.members
					.filter((member) => {
						const settings = duplicateVar(member?.settings);
						const isAssigned = settings?.assign_to_all_product ?? false;
						return isAssigned;
					})
					.map(member => ({
						_id: member._id,
						name: member.name,
						role: member.role,
					}));

				this.belongsTo.push(...assignedMembers);
			}
		},
	},
	created() {
		if (this.show && this.requestData) {
			this.fetchData();
		}
	},
};
</script>
